import React, { useMemo, useState } from 'react'
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { Table } from 'components/core-ui/Table'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/ro'

import { addParameter, deleteParameter, fetchParameters, updateParameter } from "../api"
import { DiscountRulesForm, DiscountRuleRow } from '../interfaces'
import { columns, toTableRowItems, toolbarActions } from './helpers'
import s from "../../shop-reception/commonStyles.modules.scss"
import { isValidYearMonth } from "../utils"

const EMPTY_RULES: DiscountRulesForm = {
  cashbackPercent: '',
  transactionsPerDay: '',
  minimumAmountDay: '',
  maximumAmountDay: '',
  minimumAmountMonth: '',
  maximumAmountMonth: '',
}

const isFutureRuleMonth = (year: number, month: number) => {
  const ruleMonth = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).startOf('month')
  const currentMonth = dayjs().startOf('month')
  return ruleMonth.isAfter(currentMonth)
}

const RuleManagementPage: React.FC = () => {
  const { showNotification } = useNotification()

  const [rules, setRules] = useState<DiscountRulesForm>(EMPTY_RULES)
  const [effectiveMonth, setEffectiveMonth] = useState<string>('')
  const [editingId, setEditingId] = useState<number | null>(null)

  const [tableItems, setTableItems] = useState<any[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [lastLoadedRows, setLastLoadedRows] = useState<DiscountRuleRow[]>([])

  const minSelectableMonth = useMemo(() => dayjs().startOf('month').add(1, 'month'), [])

  const effectiveMonthValue: Dayjs | null = useMemo(() => {
    if (!effectiveMonth || !isValidYearMonth(effectiveMonth)) return null
    return dayjs(`${effectiveMonth}-01`)
  }, [effectiveMonth])

  const handleEffectiveMonthChange = (val: Dayjs | null) => {
    if (!val || !val.isValid()) {
      setEffectiveMonth('')
      return
    }
    setEffectiveMonth(val.format('YYYY-MM'))
  }

  const handleChange =
    (field: keyof DiscountRulesForm) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = (event.target as HTMLInputElement).value

        if (raw === '') {
          setRules(prev => ({ ...prev, [field]: '' }))
          return
        }

        const n = Number(raw)

        if (Number.isNaN(n)) return

        setRules(prev => ({ ...prev, [field]: Math.max(0, n) }))
      }

  const blockMinus: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault()
  }

  const isFormValid = effectiveMonth && Object.values(rules).every(v => v !== '')

  const fetchData = async ({ page, rowsPerPage /*, order, orderBy, tableFilters */ }) => {
    setIsLoading(true)
    try {
      const res = await fetchParameters({ page, size: rowsPerPage })
      const list = res.list ?? []

      setLastLoadedRows(list)
      setTableItems(toTableRowItems(list))
      setTotalCount(res.page?.totalCount ?? list.length)
    } catch (error) {
      showNotification({
        message: `Error while fetching parameters. ${formatErrorMessage(error)}`,
        type: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleEditRow = (row: DiscountRuleRow) => {
    if (!isFutureRuleMonth(row.year, row.month)) {
      showNotification({
        type: 'warning',
        message: 'Nu poți edita o regulă din luna curentă sau din trecut. Doar reguli viitoare.',
      })
      return
    }

    setEditingId(row.id)
    setRules({
      cashbackPercent: row.cashbackPercent,
      transactionsPerDay: row.transactionsPerDay,
      minimumAmountDay: row.minimumAmountDay,
      maximumAmountDay: row.maximumAmountDay,
      minimumAmountMonth: row.minimumAmountMonth,
      maximumAmountMonth: row.maximumAmountMonth,
    })
    setEffectiveMonth(`${row.year}-${String(row.month).padStart(2, '0')}`)

    setTimeout(() => document.getElementById('cashbackInputRef')?.focus(), 0)

    showNotification({
      type: 'info',
      message: `Editezi regula pentru ${row.year}-${String(row.month).padStart(2, '0')}.`,
    })
  }

  const handleDeleteRow = async (row: DiscountRuleRow) => {
    if (!isFutureRuleMonth(row.year, row.month)) {
      showNotification({
        type: 'warning',
        message: 'Nu poți șterge o regulă din luna curentă sau din trecut. Doar reguli viitoare.',
      })
      return
    }

    const ok = window.confirm('Sigur vrei să ștergi această regulă?')
    if (!ok) return

    setIsLoading(true)
    try {
      await deleteParameter(row.id)

      showNotification({
        type: 'success',
        message: 'Regula a fost ștearsă.',
      })

      setShouldRefreshData(true)

      if (editingId === row.id) {
        cancelEdit()
      }
    } catch (error) {
      showNotification({
        type: 'error',
        message: `A apărut o eroare la ștergere. ${formatErrorMessage(error)}`,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const cancelEdit = () => {
    setRules(EMPTY_RULES)
    setEffectiveMonth('')
    setEditingId(null)
  }

  const handleSave = async () => {
    if (!effectiveMonth) {
      showNotification({
        type: 'error',
        message: 'Luna de activare este obligatorie.',
      })
      return
    }

    if (!isValidYearMonth(effectiveMonth)) {
      showNotification({
        type: 'error',
        message: 'Luna de activare trebuie să fie în format YYYY-MM (ex: 2026-02).',
      })
      return
    }

    if (!Object.values(rules).every(v => v !== '')) {
      showNotification({
        type: 'error',
        message: 'Completează toate câmpurile.',
      })
      return
    }

    if (Object.values(rules).some(v => v !== '' && Number(v) < 0)) {
      showNotification({ type: 'error', message: 'Valorile nu pot fi negative.' })
      return
    }

    const [yStr, mStr] = effectiveMonth.split('-')
    const year = Number(yStr)
    const month = Number(mStr)

    if (!isFutureRuleMonth(year, month)) {
      const currentYm = dayjs().format('YYYY-MM')
      showNotification({
        type: 'error',
        message: `Poți adăuga/modifica reguli doar pentru luni viitoare. Luna curentă este ${currentYm}.`,
      })
      return
    }

    const payloadBase = {
      cashbackPercent: rules.cashbackPercent as number,
      transactionsPerDay: rules.transactionsPerDay as number,
      minimumAmountDay: rules.minimumAmountDay as number,
      maximumAmountDay: rules.maximumAmountDay as number,
      minimumAmountMonth: rules.minimumAmountMonth as number,
      maximumAmountMonth: rules.maximumAmountMonth as number,
      year,
      month,
    }

    const duplicateRule = lastLoadedRows.find(r => r.year === year && r.month === month)

    const isDuplicate =
      editingId == null
        ? !!duplicateRule
        : !!duplicateRule && duplicateRule.id !== editingId

    if (isDuplicate) {
      showNotification({
        type: 'error',
        message: `Există deja o regulă pentru luna ${effectiveMonth}. Alege o altă lună.`,
      })
      return
    }

    setIsLoading(true)
    try {
      if (editingId == null) {
        await addParameter(payloadBase)
        showNotification({ type: 'success', message: `Regula a fost adăugată pentru ${effectiveMonth}.` })
      } else {
        await updateParameter({ id: editingId, ...payloadBase })
        showNotification({ type: 'success', message: `Regula a fost actualizată pentru ${effectiveMonth}.` })
      }

      setShouldRefreshData(true)
      cancelEdit()
    } catch (error) {
      showNotification({
        type: 'error',
        message: `A apărut o eroare la salvare. ${formatErrorMessage(error)}`,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const toolbar = useMemo(
    () =>
      toolbarActions({
        onCancelEdit: editingId ? cancelEdit : undefined,
        isEditing: !!editingId,
      }),
    [totalCount, editingId]
  )

  return (
    <div className={s.mainContainer}>
      <Typography variant="medium" component="h2" style={{ textAlign: 'center' }}>
        Discount angajați – Management reguli
      </Typography>

      <div style={{ marginTop: 12 }}>
        <p>
          Utilizatorii de business pot ajusta parametrii de calcul și pot programa când intră în vigoare noile reguli.
        </p>
      </div>

      <div style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 24 }}>Editare parametri</h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '25px',
            columnGap: '85px',
          }}
        >
          <Input
            type="number"
            label="Procent cashback (%)"
            id="cashbackInputRef"
            variant="standard"
            width="100%"
            value={rules.cashbackPercent}
            onChange={handleChange('cashbackPercent')}
            onKeyDown={blockMinus}
          />
          <Input
            type="number"
            label="Număr tranzacții/zi"
            variant="standard"
            width="100%"
            value={rules.transactionsPerDay}
            onChange={handleChange('transactionsPerDay')}
            onKeyDown={blockMinus}
          />
          <Input
            type="number"
            label="Valoare minimă/zi/tranzactie (RON)"
            variant="standard"
            width="100%"
            value={rules.minimumAmountDay}
            onChange={handleChange('minimumAmountDay')}
            onKeyDown={blockMinus}
          />
          <Input
            type="number"
            label="Valoare maximă/zi (RON)"
            variant="standard"
            width="100%"
            value={rules.maximumAmountDay}
            onChange={handleChange('maximumAmountDay')}
            onKeyDown={blockMinus}
          />
          <Input
            type="number"
            label="Valoare minimă/lună (RON)"
            variant="standard"
            width="100%"
            value={rules.minimumAmountMonth}
            onChange={handleChange('minimumAmountMonth')}
            onKeyDown={blockMinus}
          />
          <Input
            type="number"
            label="Valoare maximă/lună (RON)"
            variant="standard"
            width="100%"
            value={rules.maximumAmountMonth}
            onChange={handleChange('maximumAmountMonth')}
            onKeyDown={blockMinus}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(340px, 480px) auto',
            columnGap: '16px',
            rowGap: '16px',
            alignItems: 'center',
            marginTop: '45px',
            maxWidth: '820px',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ro">
            <DatePicker
              label="Luna de activare"
              views={['year', 'month']}
              openTo="month"
              value={effectiveMonthValue}
              onChange={handleEffectiveMonthChange}
              format="YYYY-MM"
              minDate={minSelectableMonth}
              slotProps={{
                textField: {
                  variant: 'standard',
                  fullWidth: true,
                  helperText: 'Selectează luna și anul',
                },
              }}
            />
          </LocalizationProvider>

          <Button
            type="button"
            variant="contained"
            label={editingId ? 'SALVEAZĂ MODIFICAREA' : 'ADAUGĂ REGULĂ'}
            onClick={handleSave}
            disabled={!isFormValid || isLoading}
            style={{ minWidth: '200px', height: 40 }}
          />
        </div>
      </div>

      <div style={{ marginTop: 40 }}>
        <h3>Reguli existente</h3>

        <Table
          hideCheckbox
          columns={columns({ onEdit: handleEditRow, onDelete: handleDeleteRow, isLoading, canMutateRow: (row) => isFutureRuleMonth(row.year, row.month) })}
          items={tableItems}
          fetchData={fetchData}
          isLoading={isLoading}
          totalCount={totalCount}
          shouldRefreshData={shouldRefreshData}
          setShouldRefreshData={setShouldRefreshData}
          toolbarActions={toolbar}
          rowActionsWidth="130px"
          // filters={[]}
          rowsPerPage={10}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>
    </div>
  )
}

export default RuleManagementPage
